import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, useCurrency, API } from "../../App";
import UserLayout from "../../components/UserLayout";
import { ArrowLeft, CheckCircle, Clock, Package, Truck, MapPin, Phone, Mail, User, FileText, Download, Wallet, ExternalLink, Camera, X, Store } from "lucide-react";
import { toast } from "sonner";
import { downloadInvoicePdf } from "../../utils/pdfDownload";
import { aggregateTaxBreakdown } from "../../utils/taxBreakdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrderTracking() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { symbol } = useCurrency();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReadyImage, setShowReadyImage] = useState(false);
  const [showRiderInfo, setShowRiderInfo] = useState(false);
  const [showKitchenInfo, setShowKitchenInfo] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchOrder();
    if (user) {
      const wsUrl = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");
      wsRef.current = new WebSocket(`${wsUrl}/ws/${user.user_id}`);
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "order_status_update" && data.order_id === orderId) {
          fetchOrder();
        }
      };
      wsRef.current.onerror = () => {};
    }
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [orderId, user]);

  const fetchOrder = async () => {
    try {
      const response = await fetch(`${API}/orders/${orderId}`, { credentials: "include" });
      if (response.ok) setOrder(await response.json());
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const downloadInvoice = async () => {
    toast.info("Preparing invoice...");
    try {
      await downloadInvoicePdf("order", orderId, `Invoice-${orderId}.pdf`);
    } catch {
      toast.error("Failed to download invoice");
    }
  };

  const isPickup = order?.order_type === "pickup";

  const statusSteps = isPickup
    ? [
        { key: "received", label: "Received", icon: CheckCircle, desc: "Order confirmed" },
        { key: "preparing", label: "In Process", icon: Clock, desc: "Getting things ready" },
        { key: "ready", label: "Ready", icon: Package, desc: "Ready for pickup" },
        { key: "pick_up", label: "Pick Up", icon: Store, desc: "Collect from kitchen" },
        { key: "delivered", label: "Delivered", icon: CheckCircle, desc: "Enjoy your meal!" },
      ]
    : [
        { key: "received", label: "Received", icon: CheckCircle, desc: "Order confirmed" },
        { key: "preparing", label: "In Process", icon: Clock, desc: "Getting things ready" },
        { key: "ready", label: "Ready", icon: Package, desc: "Ready for pickup" },
        { key: "out_for_delivery", label: "On the way", icon: Truck, desc: "Out for delivery" },
        { key: "delivered", label: "Delivered", icon: MapPin, desc: "You've made our day!" },
      ];

  const getStatusIndex = (status) => statusSteps.findIndex((s) => s.key === status);
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const getStatusTimestamp = (statusKey) => {
    const history = order?.status_history || [];
    const entry = history.find((h) => h.status === statusKey);
    return entry ? { time: new Date(entry.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }), updatedBy: entry.updated_by } : null;
  };

  if (loading) {
    return <UserLayout showHeader={false}><div className="p-4 space-y-3"><div className="skeleton h-6 w-32 rounded"></div><div className="skeleton h-40 w-full rounded-xl"></div></div></UserLayout>;
  }

  if (!order) {
    return <UserLayout showHeader={false}><div className="p-4 text-center"><p className="text-sm text-gray-500">Order not found</p><button onClick={() => navigate("/orders")} className="text-sm text-green-600 font-semibold mt-3">View All Orders</button></div></UserLayout>;
  }

  const currentStatusIndex = getStatusIndex(order.status);
  const googleMapsUrl = order.delivery_lat && order.delivery_lng
    ? `https://www.google.com/maps?q=${order.delivery_lat},${order.delivery_lng}`
    : `https://www.google.com/maps/search/${encodeURIComponent(order.delivery_address)}`;

  return (
    <UserLayout showHeader={false} showNav={false}>
      <div className="animate-fade-in" data-testid="order-tracking">
        {/* Header */}
        <div className="bg-gradient-to-br from-green-600 to-green-700 text-white p-4">
          <button onClick={() => navigate("/orders")} className="mb-3 text-green-200"><ArrowLeft size={18} /></button>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-green-200 font-semibold">Track Order</p>
              {order.order_number && <p className="text-lg font-extrabold" data-testid="tracking-order-number">{order.order_number}</p>}
              <p className="text-[10px] text-green-200">#{order.order_id}</p>
              {isPickup && <span className="inline-block mt-1 text-[10px] font-bold bg-white/20 text-white px-2 py-0.5 rounded-full" data-testid="pickup-order-badge">Pickup Order</span>}
            </div>
            <button onClick={downloadInvoice} className="bg-white/20 p-2.5 rounded-xl backdrop-blur-sm" data-testid="download-invoice"><Download size={16} /></button>
          </div>
        </div>

        {/* Wallet Balance After Order */}
        <div className="p-3 flex items-center justify-between bg-green-50 dark:bg-green-900/10 border-b border-green-100 dark:border-green-800/20">
          <div className="flex items-center gap-2"><Wallet size={14} className="text-green-600" /><span className="text-[10px] text-gray-400">Wallet Balance</span></div>
          <div className="text-right">
            <span className="text-base font-extrabold text-green-600" data-testid="wallet-after-order">{symbol}{Number(user?.wallet_balance || 0).toFixed(2)}</span>
            <p className="text-[9px] text-gray-400">after {symbol}{Number(order.total_amount || 0).toFixed(2)} deduction</p>
          </div>
        </div>

        {/* Status Tracker */}
        <div className="p-5 border-b border-gray-100 dark:border-gray-800">
          <div className="status-tracker">
            {statusSteps.map((step, index) => {
              const isCompleted = index < currentStatusIndex;
              const isActive = index === currentStatusIndex;
              const isPending = index > currentStatusIndex;
              const Icon = step.icon;
              return (
                <div key={step.key} className={`status-step ${isCompleted ? "completed" : ""} ${isActive ? "active" : ""}`} data-testid={`status-${step.key}`}>
                  <div className="status-dot"><Icon size={10} /></div>
                  <div>
                    <p className={`text-sm ${isPending ? 'text-gray-300 dark:text-gray-600' : 'text-gray-900 dark:text-white font-semibold'}`}>{step.label}</p>
                    <p className={`text-xs ${isPending ? 'text-gray-500 dark:text-gray-700' : 'text-gray-500 dark:text-gray-400'}`}>{step.desc}</p>
                    {!isPending && getStatusTimestamp(step.key) && (
                      <p className="text-[10px] text-green-600 font-medium mt-0.5">
                        {getStatusTimestamp(step.key).time}
                        {getStatusTimestamp(step.key).updatedBy && <span className="text-blue-500 ml-1">by {getStatusTimestamp(step.key).updatedBy}</span>}
                      </p>
                    )}
                    {step.key === "ready" && !isPending && order.ready_image_url && (
                      <button
                        onClick={() => setShowReadyImage(true)}
                        className="flex items-center gap-1 mt-1 text-[11px] font-semibold text-green-600 hover:text-green-700 transition-colors"
                        data-testid="view-ready-image-link"
                      >
                        <Camera size={11} /> View Photo
                      </button>
                    )}
                    {(step.key === "out_for_delivery" || step.key === "pick_up") && !isPending && order.rider && !isPickup && (
                      <button
                        onClick={() => setShowRiderInfo(true)}
                        className="flex items-center gap-1 mt-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                        data-testid="view-rider-info-link"
                      >
                        <User size={11} /> Rider Details
                      </button>
                    )}
                    {step.key === "pick_up" && !isPending && isPickup && order.kitchen_id && (
                      <button
                        onClick={() => setShowKitchenInfo(true)}
                        className="flex items-center gap-1 mt-1 text-[11px] font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                        data-testid="view-kitchen-info-link"
                      >
                        <Store size={11} /> Kitchen Details
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Ready Image Popup */}
        {showReadyImage && order.ready_image_url && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowReadyImage(false)} data-testid="ready-image-popup">
            <div className="relative max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setShowReadyImage(false)} className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg z-10" data-testid="close-ready-image-popup">
                <X size={16} className="text-gray-600" />
              </button>
              <img src={`${BACKEND_URL}${order.ready_image_url}`} alt="Order ready" className="w-full rounded-xl shadow-2xl" data-testid="ready-image" />
              <p className="text-center text-white text-xs mt-3 font-medium">Your order is ready!</p>
            </div>
          </div>
        )}

        {/* Rider Info Popup */}
        {showRiderInfo && order.rider && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowRiderInfo(false)} data-testid="rider-info-popup">
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl p-5 max-w-xs w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setShowRiderInfo(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600" data-testid="close-rider-info-popup">
                <X size={16} />
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <Truck size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Delivery Partner</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{order.rider.rider_name || "Assigned Rider"}</p>
                </div>
              </div>
              <div className="space-y-3">
                <a href={order.rider.rider_phone ? `tel:${order.rider.rider_phone}` : undefined} className={`flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl ${order.rider.rider_phone ? "hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" : ""}`} data-testid="rider-popup-call">
                  <Phone size={15} className="text-blue-500" />
                  <span className="text-sm font-medium text-blue-600">{order.rider.rider_phone || "Phone not available"}</span>
                </a>
                {order.rider.tracking_link && (
                  <a href={order.rider.tracking_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors" data-testid="rider-popup-track">
                    <MapPin size={15} className="text-green-500" />
                    <span className="text-sm font-medium text-green-600">Track Live Location</span>
                    <ExternalLink size={11} className="text-green-400 ml-auto" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Kitchen Info Popup (Pickup Orders) */}
        {showKitchenInfo && order.kitchen_id && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowKitchenInfo(false)} data-testid="kitchen-info-popup">
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl p-5 max-w-xs w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setShowKitchenInfo(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600" data-testid="close-kitchen-info-popup">
                <X size={16} />
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                  <Store size={18} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Pickup Kitchen</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white" data-testid="kitchen-popup-name">{order.kitchen_code || order.kitchen_name || order.kitchen_business_name}</p>
                </div>
              </div>
              <div className="space-y-3">
                {(order.kitchen_city || order.kitchen_locality) && (
                  <div className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <MapPin size={15} className="text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300" data-testid="kitchen-popup-location">
                      {[order.kitchen_locality, order.kitchen_city].filter(Boolean).join(", ")}
                    </span>
                  </div>
                )}
                {order.kitchen_address && (
                  <div className="flex items-start gap-3 p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <Store size={15} className="text-gray-400 shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-600 dark:text-gray-400" data-testid="kitchen-popup-address">{order.kitchen_address}</span>
                  </div>
                )}
                {order.kitchen_phone && (
                  <a href={`tel:${order.kitchen_phone}`} className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" data-testid="kitchen-popup-phone">
                    <Phone size={15} className="text-blue-500" />
                    <span className="text-sm font-medium text-blue-600">{order.kitchen_phone}</span>
                  </a>
                )}
                {(order.kitchen_google_maps_link || (order.kitchen_lat && order.kitchen_lng)) && (
                  <a href={order.kitchen_google_maps_link || `https://www.google.com/maps?q=${order.kitchen_lat},${order.kitchen_lng}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors" data-testid="kitchen-popup-map">
                    <MapPin size={15} className="text-green-500" />
                    <span className="text-sm font-medium text-green-600">Open in Google Maps</span>
                    <ExternalLink size={11} className="text-green-400 ml-auto" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delivery/Pickup Code */}
        {order.delivery_code && (
          <div className="p-4 border-b border-gray-100 dark:border-gray-800">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">{isPickup ? "Pickup Code" : "Delivery Code"}</p>
            <div className="bg-gray-900 dark:bg-gray-800 rounded-xl p-3 text-center">
              <p className="text-2xl font-mono font-extrabold tracking-[0.3em] text-green-400" data-testid="delivery-code">{order.delivery_code}</p>
              <p className="text-[9px] text-gray-500 mt-1">{isPickup ? "Share this code when collecting your order" : "Share this code with your delivery partner"}</p>
            </div>
          </div>
        )}

        {/* Order Items */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-800">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-3">Order Details</p>
          <div className="space-y-2">
            {order.items.map((item, idx) => (
              <div key={idx}>
                <div className="flex justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.name} x{item.quantity}</p>
                    {item.selected_variations?.length > 0 && (() => {
                      const groups = {};
                      item.selected_variations.forEach(v => {
                        const g = v.variation_name || "Variation";
                        groups[g] = groups[g] || [];
                        groups[g].push(v.option_value);
                      });
                      return Object.entries(groups).map(([g, vals]) => (
                        <p key={g} className="text-[9px] text-gray-400">{g}: {vals.join(", ")}</p>
                      ));
                    })()}
                    {item.selected_addons?.length > 0 && (() => {
                      const groups = {};
                      item.selected_addons.forEach(a => {
                        const g = a.group_name || "Add-ons";
                        groups[g] = groups[g] || [];
                        groups[g].push(a.name);
                      });
                      return Object.entries(groups).map(([g, names]) => (
                        <p key={g} className="text-[9px] text-gray-400">+ {g}: {names.join(", ")}</p>
                      ));
                    })()}
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{symbol}{Number(item.subtotal || 0).toFixed(2)}</span>
                </div>
                {item.tax_amount > 0 && <p className="text-[9px] text-gray-400 text-right">Tax: {symbol}{Number(item.tax_amount).toFixed(2)} ({item.tax_details?.map(t => `${t.name} ${t.percentage}%`).join(", ")})</p>}
              </div>
            ))}
          </div>
          <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-800 space-y-1">
            {(() => {
              const sub = Number(order.subtotal || 0);
              const disc = Number(order.discount_amount || 0);
              const tax = Number(order.total_tax || 0);
              const total = Number(order.total_amount || 0);
              const roundOff = total - (sub - disc + tax);
              const breakdown = aggregateTaxBreakdown(order.items || []);
              return (
                <>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Base Amount</span><span className="text-gray-600 dark:text-gray-300" data-testid="tracking-base">{symbol}{sub.toFixed(2)}</span></div>
                  {disc > 0 && (
                    <div className="flex justify-between text-sm"><span className="text-green-600 font-medium">{order.discount_label || "App Only Discount"}</span><span className="text-green-600 font-semibold" data-testid="tracking-discount">-{symbol}{disc.toFixed(2)}</span></div>
                  )}
                  {breakdown.length > 0 ? (
                    breakdown.map((tx) => tx.amount > 0 && (
                      <div key={`${tx.name}-${tx.percentage}`} className="flex justify-between text-sm" data-testid={`tracking-tax-${tx.name}`}>
                        <span className="text-gray-500">{tx.name} {tx.percentage}%</span>
                        <span className="text-gray-600 dark:text-gray-300">{symbol}{Number(tx.amount).toFixed(2)}</span>
                      </div>
                    ))
                  ) : (
                    tax > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-gray-500">Tax</span><span className="text-gray-600 dark:text-gray-300" data-testid="tracking-tax">{symbol}{tax.toFixed(2)}</span></div>
                    )
                  )}
                  {Math.abs(roundOff) >= 0.01 && (
                    <div className="flex justify-between text-xs"><span className="text-gray-400">Round Off</span><span className="text-gray-500" data-testid="tracking-rounding">{roundOff > 0 ? "+" : "-"}{symbol}{Math.abs(roundOff).toFixed(2)}</span></div>
                  )}
                  <div className="flex justify-between pt-1 border-t border-gray-100 dark:border-gray-800 text-base">
                    <span className="font-bold text-gray-900 dark:text-white">Total</span>
                    <span className="font-extrabold text-green-600" data-testid="tracking-total">{symbol}{total.toFixed(2)}</span>
                  </div>
                </>
              );
            })()}
            {order.payment_method && (
              <div className="flex justify-between text-xs"><span className="text-gray-400">Paid via</span><span className="text-gray-500 font-medium capitalize">{order.payment_method === "gateway" ? "Online Payment" : "Wallet"}</span></div>
            )}
          </div>
        </div>

        {/* Contact & Delivery */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-800">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-3">{isPickup ? "Contact & Pickup" : "Contact & Delivery"}</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2"><User size={13} className="text-gray-400" /><span className="text-sm text-gray-900 dark:text-white">{order.user_name}</span></div>
            {order.user_phone && <div className="flex items-center gap-2"><Phone size={13} className="text-gray-400" /><span className="text-sm text-gray-600 dark:text-gray-300">{order.user_phone}</span></div>}
            <div className="flex items-center gap-2"><Mail size={13} className="text-gray-400" /><span className="text-sm text-gray-600 dark:text-gray-300">{order.user_email}</span></div>
            <div className="flex items-start gap-2">
              {isPickup ? <Store size={13} className="text-green-600 flex-shrink-0 mt-0.5" /> : <MapPin size={13} className="text-green-600 flex-shrink-0 mt-0.5" />}
              <div>
                <p className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold mb-0.5">{isPickup ? "Pickup Location" : "Delivery Address"}</p>
                {isPickup && (
                  <div className="space-y-1 mb-1">
                    {order.kitchen_code && <p className="text-sm font-bold text-gray-900 dark:text-white">{order.kitchen_code}{order.kitchen_locality ? ` — ${order.kitchen_locality}` : ''}{order.kitchen_city ? `, ${order.kitchen_city}` : ''}</p>}
                    {order.kitchen_business_name && <p className="text-xs font-medium text-gray-700 dark:text-gray-200">{order.kitchen_business_name}</p>}
                    <p className="text-sm text-gray-900 dark:text-white">{order.delivery_address}</p>
                    {order.kitchen_phone && (
                      <a href={`tel:${order.kitchen_phone}`} className="flex items-center gap-1.5 text-xs text-green-700 font-semibold">
                        <Phone size={11} className="text-green-600" />{order.kitchen_phone}
                      </a>
                    )}
                  </div>
                )}
                {!isPickup && <p className="text-sm text-gray-900 dark:text-white">{order.delivery_address}</p>}
                <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 mt-1 text-xs text-green-600 font-semibold" data-testid="location-link">
                  <ExternalLink size={10} /> Open in Google Maps
                </a>
              </div>
            </div>
            {order.billing_address && (
              <div className="flex items-start gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                <FileText size={13} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold mb-0.5">Billing Address</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{order.billing_address}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Order Notes */}
        {(order.order_notes || order.delivery_instructions) && (
          <div className="p-4 border-b border-gray-100 dark:border-gray-800">
            {order.order_notes && <div className="mb-2"><p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1"><FileText size={9} className="inline mr-1" />Order Notes</p><p className="text-sm text-gray-600 dark:text-gray-300 bg-yellow-50 dark:bg-yellow-900/10 p-2 rounded-lg">{order.order_notes}</p></div>}
            {order.delivery_instructions && <div><p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Delivery Instructions</p><p className="text-sm text-gray-600 dark:text-gray-300">{order.delivery_instructions}</p></div>}
          </div>
        )}

        {/* Support */}
        <div className="p-4 pb-6">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-3">Need Help?</p>
          <a href="tel:+919621886633" className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-800/20 rounded-xl" data-testid="support-link">
            <Phone size={16} className="text-green-600" />
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Contact Support</p>
              <p className="text-[10px] text-gray-400">Tap to call +91 96218 86633</p>
            </div>
          </a>
          <p className="text-center mt-4 text-[10px] text-gray-500">Ordered on {formatDate(order.created_at)}</p>
        </div>
      </div>
    </UserLayout>
  );
}
